// Focus Trap Hook for Modal Components
// Implements WCAG 2.1 AA focus management for dialogs and modals
// Uses react-focus-lock for robust focus trapping

import { useEffect } from 'react'
import type React from 'react'

export interface FocusTrapOptions {
  /** Whether the focus trap is active */
  isActive: boolean
  /** Element that triggered the modal (to return focus on close) */
  triggerElement?: HTMLElement | null
  /** Callback when Escape key is pressed */
  onEscapePress?: () => void
  /** Allow Escape key to close */
  allowEscape?: boolean
  /** Initial element to focus when trap activates */
  initialFocusRef?: React.RefObject<HTMLElement>
  /** Whether to return focus to trigger element on deactivate */
  returnFocus?: boolean
}

/**
 * Hook for managing focus trap in modals and dialogs
 *
 * Features:
 * - Captures focus when modal opens
 * - Prevents Tab from escaping modal
 * - Returns focus to trigger element on close
 * - Escape key support
 * - WCAG 2.1 AA compliant
 *
 * @example
 * ```tsx
 * function Modal({ isOpen, onClose, children }) {
 *   const modalRef = useRef<HTMLDivElement>(null)
 *   const firstFocusRef = useRef<HTMLButtonElement>(null)
 *
 *   useFocusTrap({
 *     isActive: isOpen,
 *     onEscapePress: onClose,
 *     initialFocusRef: firstFocusRef,
 *     returnFocus: true
 *   })
 *
 *   if (!isOpen) return null
 *
 *   return (
 *     <div ref={modalRef} role="dialog" aria-modal="true">
 *       <button ref={firstFocusRef} onClick={onClose}>Close</button>
 *       {children}
 *     </div>
 *   )
 * }
 * ```
 */
export function useFocusTrap(options: FocusTrapOptions) {
  const {
    isActive,
    triggerElement,
    onEscapePress,
    allowEscape = true,
    initialFocusRef,
    returnFocus = true,
  } = options

  // Store the trigger element when trap activates
  useEffect(() => {
    if (!isActive) return

    // Capture currently focused element as trigger if not provided
    const activeTrigger = triggerElement || (document.activeElement as HTMLElement)

    // Focus initial element if provided
    if (initialFocusRef?.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initialFocusRef.current?.focus()
      }, 0)
    }

    // Return focus on cleanup
    return () => {
      if (returnFocus && activeTrigger) {
        // Delay to avoid focus conflicts
        setTimeout(() => {
          activeTrigger.focus()
        }, 0)
      }
    }
  }, [isActive, triggerElement, initialFocusRef, returnFocus])

  // Handle Escape key
  useEffect(() => {
    if (!isActive || !allowEscape) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onEscapePress?.()
      }
    }

    document.addEventListener('keydown', handleEscape, { capture: true })

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true })
    }
  }, [isActive, allowEscape, onEscapePress])
}

/**
 * Hook for managing Tab key navigation within a container
 * Alternative to react-focus-lock for simpler cases
 */
export function useTabTrap(containerRef: React.RefObject<HTMLElement>, isActive: boolean) {
  useEffect(() => {
    if (!isActive || !containerRef.current) return

    const container = containerRef.current

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )

      const focusableArray = Array.from(focusableElements)
      const firstElement = focusableArray[0]
      const lastElement = focusableArray[focusableArray.length - 1]

      if (!firstElement || !lastElement) return

      // Shift + Tab on first element -> focus last element
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      // Tab on last element -> focus first element
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
        return
      }
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [containerRef, isActive])
}
